// pages/setup/components/SetupSidebar.tsx
import {
    MonitorPlay,
    Grid3x3,
    Anchor,
    Award,
    Film,
    Loader2,
    ChevronRight,
    Eye,
    EyeOff,
    FileText,
    Plus,
    Trash2,
    AlertCircle,
    Info
} from 'lucide-react';
import { summarizeDraft, MAX_PAGE_OVERRIDES, type PageOverrideDraft } from '../pageOverrides';

export type SetupSidebarTab = 'blocks' | 'config' | 'pages';

interface BlockConfig {
    name: string;
    enabled: boolean;
}

interface SetupSidebarProps {
    selectedBlock: string;
    onSelectBlock: (blockName: string) => void;
    blocks: Record<string, BlockConfig>;
    selectedTab: SetupSidebarTab;
    onSelectTab: (tab: SetupSidebarTab) => void;
    pageOverrides: PageOverrideDraft[];
    selectedOverrideUid: string | null;
    /** Regras com erro de validação, para marcar na lista. */
    overrideErrorUids: Set<string>;
    onSelectOverride: (uid: string) => void;
    onAddOverride: () => void;
    onRemoveOverride: (uid: string) => void;
}

interface BlockGroup {
    id: string;
    title: string;
    icon: any;
    blocks: {
        name: string;
        label: string;
        icon: any;
        color: string;
    }[];
}

const TABS: { id: SetupSidebarTab; label: string }[] = [
    { id: 'blocks', label: 'Blocos' },
    { id: 'config', label: 'Config' },
    { id: 'pages', label: 'Páginas' },
];

export function SetupSidebar({
    selectedBlock,
    onSelectBlock,
    blocks,
    selectedTab,
    onSelectTab,
    pageOverrides,
    selectedOverrideUid,
    overrideErrorUids,
    onSelectOverride,
    onAddOverride,
    onRemoveOverride,
}: SetupSidebarProps) {

    const blockGroups: BlockGroup[] = [
        {
            id: 'displays',
            title: 'Blocos de Display',
            icon: MonitorPlay,
            blocks: [
                { name: 'Topo', label: 'Topo', icon: MonitorPlay, color: 'text-blue-600' },
                { name: 'Content1', label: 'Content 1', icon: Grid3x3, color: 'text-green-600' },
                { name: 'Content2', label: 'Content 2', icon: Grid3x3, color: 'text-purple-600' },
            ]
        },
        {
            id: 'sticky',
            title: 'Blocos Fixos',
            icon: Anchor,
            blocks: [
                { name: 'anchor', label: 'Anchor', icon: Anchor, color: 'text-cyan-600' },
                { name: 'fixed', label: 'Fixed', icon: Anchor, color: 'text-indigo-600' },
            ]
        },
        {
            id: 'fullscreen',
            title: 'Blocos 1x1',
            icon: Film,
            blocks: [
                { name: 'reward', label: 'Offerwall', icon: Award, color: 'text-pink-600' },
                { name: 'interstitial', label: 'Interstitial', icon: Film, color: 'text-red-600' },
                { name: 'trigger', label: 'Trigger', icon: Film, color: 'text-yellow-600' },
            ]
        }
    ];

    return (
        <div className="h-full flex flex-col bg-white border-r border-gray-200 relative z-10">
            {/* Tabs */}
            <div className="border-b border-gray-200">
                <div className="flex">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => onSelectTab(tab.id)}
                            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                                selectedTab === tab.id
                                    ? 'border-b-2 border-green-600 text-green-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {tab.label}
                            {tab.id === 'pages' && pageOverrides.length > 0 && (
                                <span className="ml-1 text-[10px] text-gray-400">({pageOverrides.length})</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
                {selectedTab === 'blocks' ? (
                    <div className="p-3 space-y-4">
                        {blockGroups.map((group) => {
                            const GroupIcon = group.icon;
                            return (
                                <div key={group.id}>
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <GroupIcon className="h-3 w-3 text-gray-500" />
                                        <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                                            {group.title}
                                        </h3>
                                    </div>
                                    <div className="space-y-0.5">
                                        {group.blocks.map((block) => {
                                            const Icon = block.icon;
                                            const isSelected = selectedBlock === block.name;
                                            const isEnabled = blocks[block.name]?.enabled ?? false;

                                            return (
                                                <button
                                                    key={block.name}
                                                    onClick={() => onSelectBlock(block.name)}
                                                    className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md transition-all text-left ${
                                                        isSelected
                                                            ? 'bg-green-50 border border-green-200'
                                                            : 'hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                                        <Icon className={`h-3 w-3 flex-shrink-0 ${block.color}`} />
                                                        <span className="text-xs font-medium text-gray-900 truncate">
                                                            {block.label}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        {isEnabled ? (
                                                            <Eye className="h-3 w-3 text-green-600" />
                                                        ) : (
                                                            <EyeOff className="h-3 w-3 text-gray-400" />
                                                        )}
                                                        {isSelected && (
                                                            <ChevronRight className="h-3 w-3 text-green-600" />
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : selectedTab === 'pages' ? (
                    <div className="p-3 space-y-3">
                        <div className="rounded-md border border-blue-200 bg-blue-50 p-2 space-y-1">
                            <p className="flex items-start gap-1.5 text-[11px] text-blue-800">
                                <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                Depois de salvar, clique em Gerar Script para a regra ir para o
                                site. Pode levar alguns minutos para aparecer (cache).
                            </p>
                            <p className="flex items-start gap-1.5 text-[11px] text-blue-800">
                                <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                Vale só uma regra por página: a mais específica (exata ganha de
                                prefixo; entre prefixos, o mais longo).
                            </p>
                        </div>

                        <button
                            onClick={onAddOverride}
                            disabled={pageOverrides.length >= MAX_PAGE_OVERRIDES}
                            className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md border border-dashed border-green-300 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Plus className="h-3 w-3" />
                            Adicionar regra
                        </button>

                        {pageOverrides.length === 0 ? (
                            <p className="text-[11px] text-gray-500 text-center py-2">
                                Nenhuma regra. Todas as páginas seguem o setup do site.
                            </p>
                        ) : (
                            <div className="space-y-0.5">
                                {pageOverrides.map((rule) => {
                                    const isSelected = selectedOverrideUid === rule.uid;
                                    const hasError = overrideErrorUids.has(rule.uid);
                                    const summary = summarizeDraft(rule);
                                    return (
                                        <div
                                            key={rule.uid}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => onSelectOverride(rule.uid)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    onSelectOverride(rule.uid);
                                                }
                                            }}
                                            className={`w-full flex items-start justify-between gap-2 px-2 py-1.5 rounded-md transition-all text-left cursor-pointer ${
                                                isSelected
                                                    ? 'bg-green-50 border border-green-200'
                                                    : hasError
                                                      ? 'border border-red-200 hover:bg-red-50'
                                                      : 'border border-transparent hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="flex items-start gap-2 flex-1 min-w-0">
                                                {hasError ? (
                                                    <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0 text-red-500" />
                                                ) : (
                                                    <FileText className="h-3 w-3 mt-0.5 flex-shrink-0 text-gray-500" />
                                                )}
                                                <div className="min-w-0 max-w-[150px]">
                                                    <p
                                                        className="text-xs font-medium text-gray-900 font-mono truncate"
                                                        title={rule.path}
                                                    >
                                                        {rule.path || '(sem path)'}
                                                    </p>
                                                    {rule.label && (
                                                        <p className="text-[10px] text-gray-600 truncate" title={rule.label}>
                                                            {rule.label}
                                                        </p>
                                                    )}
                                                    <p className="text-[10px] text-gray-400 truncate" title={summary}>
                                                        {summary}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onRemoveOverride(rule.uid);
                                                }}
                                                title="Remover regra"
                                                aria-label="Remover regra"
                                                className="p-0.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="p-3 space-y-0.5">
                        <button
                            onClick={() => onSelectBlock('lazyLoad')}
                            className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md transition-all ${
                                selectedBlock === 'lazyLoad'
                                    ? 'bg-green-50 border border-green-200'
                                    : 'hover:bg-gray-50'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <Loader2 className="h-3 w-3 text-gray-600" />
                                <span className="text-xs font-medium text-gray-900">
                                    Lazy Load
                                </span>
                            </div>
                            {selectedBlock === 'lazyLoad' && (
                                <ChevronRight className="h-3 w-3 text-green-600" />
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}